import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Shield, Users, Crown, UserCheck, UserX, Lock, Download, FileText, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Navigate, useNavigate } from "react-router-dom";
import { BlockToggle, LoginsButton, AdminJournal, BackupButton } from "@/components/AdminUserTools";

export const AdminPage = () => {
  const { API, user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });
  const [pwdSubmitting, setPwdSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/users`);
      setUsers(data);
    } catch {
      setUsers([]);
    } finally { setLoading(false); }
  }, [API]);

  const loadDownloads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/downloads`);
      setDownloads(data);
    } catch {
      setDownloads([]);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user?.is_super_admin) loadDownloads(); }, [user, loadDownloads]);

  if (user === null) return null;
  const isSuper = !!user?.is_super_admin;
  const isAdmin = user?.role === "admin" || isSuper;
  if (!user || user === false || !isAdmin) return <Navigate to="/" replace />;

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (pwdForm.next.length < 8) { toast.error("Le nouveau mot de passe doit comporter au moins 8 caractères"); return; }
    if (pwdForm.next !== pwdForm.confirm) { toast.error("Les deux nouveaux mots de passe ne correspondent pas"); return; }
    setPwdSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwdForm.current,
        new_password: pwdForm.next,
      });
      toast.success("Mot de passe modifié. Vous allez être déconnecté·e.");
      setShowPwdModal(false);
      // Attendre un peu pour que l'utilisateur voie le message, puis déconnexion forcée
      setTimeout(async () => {
        await logout();
        navigate("/login");
      }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de changement de mot de passe");
    } finally {
      setPwdSubmitting(false);
    }
  };

  const changeRole = async (uid, role) => {
    try {
      await axios.put(`${API}/users/${uid}/role`, { role });
      toast.success("Rôle mis à jour / Role updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur / Error");
    }
  };

  const validateMember = async (uid) => {
    try {
      await axios.put(`${API}/users/${uid}/validate`);
      toast.success("Membre validé / Member validated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const revokeMember = async (uid) => {
    if (!window.confirm("Révoquer la validation de ce membre ? Il perdra l'accès aux données privées.")) return;
    try {
      await axios.put(`${API}/users/${uid}/revoke`);
      toast.success("Validation révoquée");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const roleLabel = (r) => r === "admin" ? t("roleAdmin") : r === "elder" ? t("roleElder") : t("roleContributor");
  const roleColor = (r) => r === "admin" ? "#993D22" : r === "elder" ? "#2C4230" : "#6B4226";

  const pendingUsers = users.filter(u => !u.validated && !u.is_super_admin);
  const validatedUsers = users.filter(u => u.validated || u.is_super_admin);

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10" data-testid="admin-page">
      <div className="label-uppercase text-[#993D22] mb-2">{t("adminPanel")}</div>
      <h1 className="font-serif-heritage text-4xl sm:text-5xl text-[#261911] mb-2 flex items-center gap-3">
        <Users className="w-10 h-10 text-[#D9822B]" /> {t("manageMembers")}
      </h1>
      {isSuper && (
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-4 pl-2 pr-6 py-2 rounded-2xl bg-[#261911] text-[#FAF7F2] shadow-md" data-testid="super-admin-badge">
            <div className="w-12 h-12 rounded-full bg-[#993D22] flex items-center justify-center ring-2 ring-[#D9822B]">
              <Crown className="w-6 h-6 text-[#F4D6A0]" />
            </div>
            <div>
              <div className="label-uppercase text-[10px] text-[#D9822B]">Super-administrateur</div>
              <div className="font-serif-heritage text-2xl leading-tight" data-testid="super-admin-name">
                {(() => { const [last, ...rest] = (user?.name || "").trim().split(/\s+/); return <><span className="tracking-wide">{(last || "").toUpperCase()}</span> {rest.join(" ")}</>; })()}
              </div>
            </div>
          </div>
          <Button
            onClick={() => { setPwdForm({current:"",next:"",confirm:""}); setShowPwdModal(true); }}
            size="sm"
            variant="outline"
            className="border-[#993D22] text-[#993D22] hover:bg-[#993D22] hover:text-[#FAF7F2]"
            data-testid="open-password-modal"
          >
            <KeyRound className="w-4 h-4 mr-1" /> Changer mon mot de passe
          </Button>
        </div>
      )}

      {showPwdModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !pwdSubmitting && setShowPwdModal(false)} data-testid="password-modal-overlay">
          <form onSubmit={submitPasswordChange} onClick={(e) => e.stopPropagation()} className="bg-[#FAF7F2] max-w-md w-full rounded-xl p-6 shadow-xl" data-testid="password-modal">
            <h3 className="font-serif-heritage text-2xl text-[#261911] mb-1 flex items-center gap-2">
              <KeyRound className="w-6 h-6 text-[#993D22]" /> Changer mon mot de passe
            </h3>
            <p className="text-xs text-[#6E5B4F] mb-4 italic">
              Après changement, toutes vos sessions ouvertes (y compris celle-ci) seront invalidées.
              Vous devrez vous reconnecter avec le nouveau mot de passe.
            </p>
            {[
              { key: "current", label: "Mot de passe actuel", testid: "current-password" },
              { key: "next", label: "Nouveau mot de passe (min 8 car.)", testid: "new-password" },
              { key: "confirm", label: "Confirmer le nouveau mot de passe", testid: "confirm-password" },
            ].map(({key, label, testid}) => (
              <div key={key} className="mb-3">
                <label className="label-uppercase text-xs text-[#6E5B4F] block mb-1">{label}</label>
                <div className="relative">
                  <input
                    type={showPwd[key] ? "text" : "password"}
                    value={pwdForm[key]}
                    onChange={(e) => setPwdForm({...pwdForm, [key]: e.target.value})}
                    className="w-full px-3 py-2 pr-10 border border-[#D9CFC2] rounded-lg bg-white text-[#261911] focus:outline-none focus:ring-2 focus:ring-[#993D22]"
                    required
                    autoComplete="new-password"
                    data-testid={testid}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd({...showPwd, [key]: !showPwd[key]})}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6E5B4F] hover:text-[#261911]"
                    tabIndex={-1}
                    aria-label={showPwd[key] ? "Masquer" : "Afficher"}
                    data-testid={`toggle-${testid}`}
                  >
                    {showPwd[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
            <div className="flex gap-2 mt-5 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPwdModal(false)}
                disabled={pwdSubmitting}
                data-testid="password-cancel"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={pwdSubmitting}
                className="bg-[#993D22] hover:bg-[#7a3019] text-[#FAF7F2]"
                data-testid="password-submit"
              >
                {pwdSubmitting ? "Changement en cours…" : "Changer et me déconnecter"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-[#6E5B4F]">{t("loading")}</div>
      ) : (
        <>
          {pendingUsers.length > 0 && (
            <section className="mb-10" data-testid="pending-members-section">
              <h2 className="font-serif-heritage text-2xl text-[#993D22] mb-3 flex items-center gap-2">
                <UserCheck className="w-6 h-6" /> Membres en attente de validation ({pendingUsers.length})
              </h2>
              <p className="text-sm text-[#6E5B4F] mb-4">
                Un membre non validé ne peut pas consulter les données généalogiques privées.
                Validez-le pour lui donner accès.
              </p>
              <ul className="space-y-3">
                {pendingUsers.map(u => (
                  <li key={u.id} className="heritage-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`pending-user-${u.id}`}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-[#FAF7F2] font-serif-heritage font-bold shrink-0 bg-[#6E5B4F]">
                      {(u.name || u.email)[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-heritage text-xl text-[#261911]">{u.name}</div>
                      <div className="text-xs text-[#6E5B4F] truncate">{u.email}</div>
                    </div>
                    <Button
                      onClick={() => validateMember(u.id)}
                      className="bg-[#2C4230] hover:bg-[#3a5540] text-[#FAF7F2]"
                      data-testid={`validate-user-${u.id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-1" /> Valider
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section data-testid="validated-members-section">
            <h2 className="font-serif-heritage text-2xl text-[#2C4230] mb-3 flex items-center gap-2">
              <Shield className="w-6 h-6" /> Membres validés ({validatedUsers.length})
            </h2>
            <ul className="space-y-3 stagger">
              {validatedUsers.map(u => (
                <li key={u.id} className="heritage-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-user-${u.id}`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-[#FAF7F2] font-serif-heritage font-bold shrink-0" style={{ background: roleColor(u.role) }}>
                    {(u.name || u.email)[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-heritage text-xl text-[#261911] flex items-center gap-2">
                      {u.name}
                      {u.is_super_admin && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#993D22] text-[#FAF7F2] text-xs" data-testid={`super-admin-tag-${u.id}`}>
                          <Crown className="w-3 h-3" /> Super
                        </span>
                      )}
                      {u.is_blocked && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs" data-testid={`blocked-tag-${u.id}`}>Bloqué</span>}
                    </div>
                    <div className="text-xs text-[#6E5B4F] truncate">{u.email}</div>
                  </div>
                  <span className="label-uppercase" style={{ color: roleColor(u.role) }}>
                    <Shield className="w-3 h-3 inline mr-1" />{roleLabel(u.role)}
                  </span>
                  {u.is_super_admin ? (
                    <span className="inline-flex items-center gap-1 text-sm text-[#6E5B4F] px-3" data-testid={`locked-${u.id}`}>
                      <Lock className="w-4 h-4" /> Protégé
                    </span>
                  ) : (
                    <>
                      <Select
                        value={u.role || "contributor"}
                        onValueChange={(r) => changeRole(u.id, r)}
                        disabled={!isSuper && u.role === "admin"}
                      >
                        <SelectTrigger className="w-44 bg-[#FAF7F2]" data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contributor">{t("roleContributor")}</SelectItem>
                          <SelectItem value="elder">{t("roleElder")}</SelectItem>
                          <SelectItem value="admin" disabled={!isSuper}>{t("roleAdmin")} {!isSuper && "🔒"}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => revokeMember(u.id)}
                        className="border-[#993D22] text-[#993D22] hover:bg-[#993D22] hover:text-[#FAF7F2]"
                        data-testid={`revoke-user-${u.id}`}
                      >
                        <UserX className="w-4 h-4" />
                      </Button>
                      <BlockToggle u={u} onDone={load} />
                      <LoginsButton u={u} />
                    </>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <AdminJournal />

          {!isSuper && (
            <p className="mt-8 text-sm text-[#6E5B4F] italic">
              Seul le super-administrateur peut nommer ou révoquer un administrateur.
            </p>
          )}

          {isSuper && (
            <section className="mt-12" data-testid="private-downloads-section">
              <h2 className="font-serif-heritage text-2xl text-[#993D22] mb-3 flex items-center gap-2">
                <Download className="w-6 h-6" /> Téléchargements privés (super-admin)
              </h2>
              <p className="text-sm text-[#6E5B4F] mb-4">
                Ces fichiers (guides, sauvegardes du code et des données) sont
                protégés par authentification super-administrateur. Ils ne sont
                pas accessibles publiquement.
              </p>
              <BackupButton onDone={loadDownloads} />
              <ul className="space-y-2">
                {downloads.map(d => {
                  const url = `${API}/admin/downloads/${encodeURIComponent(d.name)}`;
                  const sizeKb = (d.size / 1024).toFixed(1);
                  const isGuide = d.name.endsWith(".md");
                  return (
                    <li key={d.name} className="heritage-card p-3 flex items-center gap-3" data-testid={`download-${d.name}`}>
                      {isGuide ? <FileText className="w-5 h-5 text-[#2C4230]" /> : <Download className="w-5 h-5 text-[#993D22]" />}
                      <span className="flex-1 font-mono text-sm text-[#261911] truncate">{d.name}</span>
                      <span className="text-xs text-[#6E5B4F]">{sizeKb} Ko</span>
                      <Button
                        onClick={async () => {
                          try {
                            const res = await axios.get(url, { responseType: "blob" });
                            const blob = new Blob([res.data]);
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(blob);
                            link.download = d.name;
                            link.click();
                            URL.revokeObjectURL(link.href);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Erreur de téléchargement");
                          }
                        }}
                        size="sm"
                        className="bg-[#993D22] hover:bg-[#7a3019] text-[#FAF7F2]"
                        data-testid={`download-btn-${d.name}`}
                      >
                        <Download className="w-4 h-4 mr-1" /> Télécharger
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default AdminPage;
